import { AutopilotState, FindInsightState } from '@cycle-app/graphql-codegen';
import { Flex, ActionButton } from '@cycle-app/ui';
import { AiIcon, CloseIcon } from '@cycle-app/ui/icons';
import { AnimatePresence } from 'framer-motion';

import { useProduct } from 'src/hooks';

import { Container, Title, Details, ProgressContainer } from './DocStateLoader.styles';
import { DocStateProgress } from './DocStateProgress';

type State = AutopilotState | FindInsightState;

const getLabel = (state: State | null | undefined) => {
  if (state === AutopilotState.WriteSummary) return 'Product summary';
  if (state === AutopilotState.FindInsights) return 'Find insights';
  if (state === AutopilotState.Categorize) return 'Categorize';
  if (state === AutopilotState.MarkProcessed) return 'Mark as processed';
  if (state === FindInsightState.FindInsights) return 'Find insights';
  if (state === FindInsightState.Categorize) return 'Categorize';
  return null;
};

type Props = {
  autopilotState: AutopilotState | null | undefined;
  findInsightState: FindInsightState | null | undefined;
  close: VoidFunction;
};

export const DocStateLoader = ({
  autopilotState, findInsightState, close,
}: Props) => {
  return (
    <AnimatePresence>
      {(autopilotState || findInsightState) && (
        <DocStateLoaderVisible
          autopilotState={autopilotState}
          findInsightState={findInsightState}
          close={close}
        />
      )}
    </AnimatePresence>
  );
};

export const DocStateLoaderVisible = ({
  autopilotState, findInsightState, close,
}: Props) => {
  const { product } = useProduct();
  const state = autopilotState ?? findInsightState;
  if (!product || !state) return null;

  let steps: (AutopilotState | FindInsightState)[] = [];
  let index = 0;

  if (autopilotState) {
    steps = [];
    if (product.writeSummary || state === AutopilotState.WriteSummary) steps.push(AutopilotState.WriteSummary);
    if (product.extractInsights || state === AutopilotState.FindInsights) steps.push(AutopilotState.FindInsights);
    if (product.linkToRoadmap || state === AutopilotState.Categorize) steps.push(AutopilotState.Categorize);
    if (product.markFeedbackAsProcessed || state === AutopilotState.MarkProcessed) steps.push(AutopilotState.MarkProcessed);
    index = steps.indexOf(state) + 1;
  }

  if (findInsightState) {
    steps = [FindInsightState.FindInsights];
    if (product.autoLinkToRoadmap || state === FindInsightState.Categorize) steps.push(FindInsightState.Categorize);
    index = steps.indexOf(state) + 1;
  }

  const title = autopilotState ? 'Feedback autopilot' : 'Find insights';

  return (
    <Container
      variants={{
        hidden: {
          opacity: 0,
          y: 20,
          x: '-50%',
        },
        visible: {
          opacity: 1,
          y: 0,
          x: '-50%',
        },
      }}
      initial="hidden"
      animate="visible"
      exit="hidden"
    >
      <ProgressContainer>
        <DocStateProgress percent={(index / steps.length) * 100} />
        <AiIcon isAnimated hasGradient />
      </ProgressContainer>

      {(state === AutopilotState.Unprocessed || state === FindInsightState.Unprocessed) ? (
        <Title>
          {`${title} is about to start…`}
        </Title>
      ) : (
        <Flex $column $align="flex-start">
          <Title>
            {`${title} in progress…`}
          </Title>
          <Details>
            {`Step ${index}/${steps.length} · ${getLabel(state)}`}
          </Details>
        </Flex>
      )}

      <ActionButton
        onClick={close}
      >
        <CloseIcon />
      </ActionButton>
    </Container>
  );
};
